import { QUOTE_AS_PER, QUOTE_AS_PER_LABELS } from '../work.constants';

/**
 * The money on a quotation, and the `POST /api/job-quotes` body built from it.
 *
 * `computeQuoteTotals` is the one definition of how a quotation adds up. It was lifted
 * out of `projectPayload.mapper`, which still uses it, so the seven-step wizard and the
 * Edit Job QUOTATION panel cannot drift into quoting the same job differently.
 */

const num = (value: unknown): number => Number(value || 0);

/** Matches `GST_RATE` in StepAccounting, which previews these same figures on screen. */
const GST_RATE = 0.18;

export interface QuoteTotals {
  /** Before the "actual vs minimum" decision — what the rate and area actually come to. */
  totalCost: number;
  /** The figure chosen on that decision, which is what the discount comes off. */
  totalAmountSelected: number;
  constructionCost: number;
  discountAmount: number;
  totalAfterDiscount: number;
  gstAmount: number;
  grossTotal: number;
}

export function computeQuoteTotals(values: Record<string, any>): QuoteTotals {
  const quote = values.quote ?? {};
  const discount = values.discount ?? {};

  let totalCost = 0;
  let totalAmountSelected = 0;
  let constructionCost = 0;

  if (quote.asPer === QUOTE_AS_PER.SQFT) {
    totalCost = num(quote.sqft?.rate) * num(quote.area);
    totalAmountSelected = num(quote.sqft?.finalValue);
  }

  if (quote.asPer === QUOTE_AS_PER.PERCENTAGE) {
    // A rate times an area when both are given, otherwise the cost typed in directly.
    constructionCost =
      quote.percentage?.constructionRate && quote.area
        ? num(quote.percentage.constructionRate) * num(quote.area)
        : num(quote.percentage?.constructionCostManual);

    totalCost = (constructionCost * num(quote.percentage?.value)) / 100;
    totalAmountSelected = num(quote.percentage?.finalValue);
  }

  if (quote.asPer === QUOTE_AS_PER.LUMPSUM) {
    totalCost = num(quote.lumpsum?.value);
    totalAmountSelected = totalCost;
  }

  const discountAmount = num(discount.value);
  // Never negative: a discount larger than the quote would otherwise invert the total.
  const totalAfterDiscount = Math.max(0, totalAmountSelected - discountAmount);
  const gstAmount = totalAfterDiscount * GST_RATE;
  // The final amount is rounded off to the rupee; GST keeps its paise.
  const grossTotal = Math.round(totalAfterDiscount + gstAmount);

  return {
    totalCost,
    totalAmountSelected,
    constructionCost,
    discountAmount,
    totalAfterDiscount,
    gstAmount,
    grossTotal,
  };
}

/** One `job_milestones` row as the analyst endpoints take it. */
export interface JobMilestonePayload {
  milestone_number: number;
  payment_percent: number;
  milestone: string;
  amount: number;
  milestone_status: string;
}

/**
 * The job's payment milestones, as rows.
 *
 * Shared by the seven-step wizard and the Edit Job QUOTATION panel so the same table on
 * screen saves the same way from either. The amount is the milestone's share of the gross
 * total — the figure the screen shows in its "Amount (incl. GST)" column — stored rather
 * than left to be re-derived later from whichever quotation happened to be in force.
 *
 * A milestone that already carries a status keeps it: re-saving the table must not undo a
 * milestone that has been achieved or invoiced.
 */
export function buildMilestoneRows(values: Record<string, any>): JobMilestonePayload[] {
  const { grossTotal } = computeQuoteTotals(values);
  const items = values.milestones?.items ?? [];

  return items.map((item: Record<string, any>, index: number) => {
    const percent = num(item.percentage);
    return {
      milestone_number: num(item.index) || index + 1,
      payment_percent: percent,
      milestone: item.label ?? '',
      amount: grossTotal ? (grossTotal * percent) / 100 : 0,
      milestone_status: item.status || 'not_started',
    };
  });
}

/**
 * The body for `POST /api/job-quotes`.
 *
 * The field names differ from the analyst job endpoint's on three keys —
 * `total_cost_after_discount`, `site_engineer_visit_fees` and `director_visit_fees` are
 * all spelled differently there (`total_after_discount`, `..._fee`). These match
 * `JobQuoteController.applyFields`, which is what reads this body.
 */
export function buildJobQuotePayload(values: Record<string, any>): Record<string, unknown> {
  const quote = values.quote ?? {};
  const siteVisit = values.siteVisit ?? {};
  const totals = computeQuoteTotals(values);

  return {
    // V35 — the letter's date and who it is addressed to; sent only by the QUOTATION panel,
    // which is the screen that asks for them.
    ...(values.quoteDate !== undefined ? { quote_date: values.quoteDate || null } : {}),
    ...(values.quoteTo !== undefined ? { quote_to: values.quoteTo || null } : {}),

    // V13 — the basis and the note now belong to the quotation itself.
    quote_as_per: QUOTE_AS_PER_LABELS[quote.asPer as keyof typeof QUOTE_AS_PER_LABELS] ?? null,
    notes: values.details?.accountingNotes || null,

    // V18 — both are read by the quotation PDF. `scope_description` is client-facing and
    // deliberately separate from `notes`, which is the internal note; `site_visit_in_scope`
    // says whether the site-visit note prints per-visit rates at all, which a zero
    // charge cannot.
    scope_description: quote.scopeDescription || null,
    site_visit_in_scope: siteVisit.inScope !== false,
    // Only meaningful while visits are in scope; the letter prints no expenses note otherwise.
    site_visit_expenses: siteVisit.inScope !== false ? siteVisit.expenses || null : null,

    total_cost: totals.totalCost,
    total_amount_selected: totals.totalAmountSelected,
    discount: totals.discountAmount,
    total_cost_after_discount: totals.totalAfterDiscount,
    gross_total: totals.grossTotal,

    quote_rate_per_sq_ft: quote.asPer === QUOTE_AS_PER.SQFT ? num(quote.sqft?.rate) : 0,
    job_area: quote.asPer !== QUOTE_AS_PER.LUMPSUM ? num(quote.area) : 0,
    construction_cost: totals.constructionCost,
    quote_percent: quote.asPer === QUOTE_AS_PER.PERCENTAGE ? num(quote.percentage?.value) : 0,

    site_engineer_visit_fees: num(siteVisit.engineer?.charge),
    site_engineer_free_visit_count: num(siteVisit.engineer?.freeVisits),
    director_visit_fees: num(siteVisit.director?.charge),
    director_free_visit_count: num(siteVisit.director?.freeVisits),
  };
}
